import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Printer, FileCsv, FilePdf } from '@phosphor-icons/react';
import { labelClass, riskClass, fmtD } from './planningUtils';

export function PlanningDayDialog({ day, onOpenChange, onAssign }) {
  if (!day) return null;
  const openReport = (format) => {
    window.open(`/api/admin/production-planning/daily-report?date=${day.date}&format=${format}`, '_blank');
  };
  return (
    <Dialog open={!!day} onOpenChange={(o) => !o && onOpenChange(null)}>
      <DialogContent className="max-w-6xl bg-white max-h-[85vh] overflow-y-auto" data-testid="planning-day-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between pr-6">
            <span>Production Plan — {fmtD(day.date)} ({day.day_of_week})</span>
            <span className="flex gap-2">
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openReport('pdf')} data-testid="day-report-pdf">
                <FilePdf size={14} className="mr-1" /> PDF
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openReport('csv')} data-testid="day-report-csv">
                <FileCsv size={14} className="mr-1" /> Excel/CSV
              </Button>
              <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => openReport('pdf')}>
                <Printer size={14} className="mr-1" /> Print
              </Button>
            </span>
          </DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-6 gap-2 mb-3">
          {[['Capacity', day.capacity], ['Confirmed', day.confirmed], ['Tentative', day.tentative],
            ['Available', day.available], ['Completed', day.completed], ['Utilization', `${day.utilization_pct}%`]].map(([k, v]) => (
            <div key={k} className="p-2 rounded-sm border border-zinc-200 bg-zinc-50 text-center">
              <div className="text-[10px] uppercase tracking-wider text-zinc-500">{k}</div>
              <div className="text-lg font-bold">{v}</div>
            </div>
          ))}
        </div>
        {day.capacity_exceeded && (
          <div className="mb-2 p-2 rounded-sm bg-red-50 border border-red-300 text-red-700 text-xs font-bold">CAPACITY EXCEEDED on this date</div>
        )}
        {day.jos.length === 0 ? (
          <div className="p-6 text-center text-sm text-zinc-400">No job orders planned on this day.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full text-xs">
              <thead className="bg-zinc-100 text-zinc-600 uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="p-2 text-left">JO No</th>
                  <th className="p-2 text-left">Customer</th>
                  <th className="p-2 text-left">Executive</th>
                  <th className="p-2 text-left">JO Date</th>
                  <th className="p-2 text-left">Delivery</th>
                  <th className="p-2 text-right">Total</th>
                  <th className="p-2 text-right">Today</th>
                  <th className="p-2 text-right">Remain</th>
                  <th className="p-2 text-left">Prod Window</th>
                  <th className="p-2 text-right">Days Left</th>
                  <th className="p-2 text-left">Status</th>
                  <th className="p-2 text-left">Type</th>
                  <th className="p-2 text-left">Action</th>
                </tr>
              </thead>
              <tbody>
                {day.jos.map((jo) => (
                  <tr key={jo.jo_number} className={`border-b border-zinc-100 ${jo.is_tentative ? 'bg-orange-50' : ''}`} data-testid={`day-jo-${jo.jo_number}`}>
                    <td className="p-2 font-mono font-bold text-blue-700">{jo.jo_number}{jo.is_urgent && <Badge className="ml-1 bg-red-600 text-white text-[9px]">URGENT</Badge>}</td>
                    <td className="p-2">{jo.customer_name}</td>
                    <td className="p-2">{jo.executive_name}</td>
                    <td className="p-2">{fmtD(jo.jo_date)}</td>
                    <td className="p-2">{fmtD(jo.delivery_date)}</td>
                    <td className="p-2 text-right font-bold">{jo.total_qty}</td>
                    <td className="p-2 text-right font-bold text-blue-700">{jo.day_qty}</td>
                    <td className="p-2 text-right">{jo.remaining_qty}</td>
                    <td className="p-2 whitespace-nowrap">{fmtD(jo.production_start)} → {fmtD(jo.production_end)}</td>
                    <td className={`p-2 text-right font-bold ${(jo.days_to_delivery ?? 99) < 0 ? 'text-red-600' : ''}`}>{jo.days_to_delivery ?? '—'}</td>
                    <td className="p-2">
                      <Badge className={`text-[10px] border ${labelClass(jo.allocation_label)}`}>{jo.allocation_label}</Badge>
                      {jo.risk !== 'none' && <span className={`ml-1 px-1 py-0.5 rounded text-[9px] font-bold uppercase ${riskClass(jo.risk)}`}>{jo.risk === 'overdue' ? 'OVERDUE' : 'DELIVERY RISK'}</span>}
                    </td>
                    <td className="p-2">
                      <Badge className={`text-[10px] ${jo.allocation_type === 'tentative' ? 'bg-orange-100 text-orange-800' : 'bg-blue-100 text-blue-800'}`}>
                        {jo.allocation_type === 'tentative' ? 'TENTATIVE' : 'CONFIRMED'}
                      </Badge>
                    </td>
                    <td className="p-2">
                      {jo.status === 'procurement_approved' ? (
                        <Button size="sm" className="h-6 text-[10px] bg-emerald-600 hover:bg-emerald-700"
                          data-testid={`day-assign-${jo.jo_number}`}
                          onClick={() => onAssign(jo.jo_number)}>Assign to Production</Button>
                      ) : jo.status === 'pending_procurement' ? (
                        <Badge className="text-[10px] bg-orange-500 text-white">Approve Procurement First</Badge>
                      ) : (
                        <span className="text-zinc-400 text-[10px]">{jo.allocation_label}</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
